/**
 * Renders a collection product card as an HTML string, mirroring the markup and
 * CSS classes of `snippets/product-item.liquid` so the existing stylesheet applies
 * unchanged. Pure function (no DOM), so it's unit-testable in Node.
 *
 * Parity scope: thumbnail, price (`$price_min` + MSRP — the Liquid card
 * shows this uniformly), badge labels, quick-ship, compare button, color swatches,
 * and the `dis_rev` Min-Qty note. Deferred (documented in docs/storefront-filtering.md):
 * marketing-promo blocks, the employee-only in-stock table, and the three-day/rush
 * labels (driven by `template_suffix`, which the Storefront API doesn't expose).
 */

import { PageContext, ProductNode } from './types';

const NEW_PRODUCT_DAYS = 60;

function esc(s: string): string {
  return (s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function numericId(gid: string): string {
  return String(gid || '').split('/').pop() || '';
}

export function formatPrice(amount?: string): string {
  const n = parseFloat(amount || '0');
  if (isNaN(n)) return '0.00';
  return n.toFixed(2);
}

export function cardImageUrl(url?: string | null, size: number = 210): string {
  if (!url) return '';
  const sep = url.indexOf('?') === -1 ? '?' : '&';
  return url + sep + 'width=' + size + '&height=' + size + '&crop=center';
}

export function handleize(s: string): string {
  return (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function daysSince(iso?: string): number {
  if (!iso) return Infinity;
  const then = Date.parse(iso);
  if (isNaN(then)) return Infinity;
  return (Date.now() - then) / 86400000;
}

interface CardFlags {
  onSale: boolean;
  quickShip: boolean;
  newProduct: boolean;
  bestSeller: boolean;
  merchPick: boolean;
  madeInUsa: boolean;
  disRevTag: string | null;
}

function readFlags(node: ProductNode): CardFlags {
  const tags = node.tags || [];
  let disRevTag: string | null = null;
  let quickShip = false;
  let bestSeller = false;
  let merchPick = false;
  let madeInUsa = false;

  for (let i = 0; i < tags.length; i++) {
    const t = tags[i];
    if (t === 'Quick Ship') quickShip = true;
    else if (t === 'shop_by=Best Seller') bestSeller = true;
    else if (t === 'shop_by=Merch Pick') merchPick = true;
    else if (t === 'csr=Made in the USA') madeInUsa = true;
    else if (!disRevTag && t.indexOf('dis_rev') === 0) disRevTag = t;
  }

  const price = node.priceRange && parseFloat(node.priceRange.minVariantPrice.amount);
  const compareAt =
    node.compareAtPriceRange && parseFloat(node.compareAtPriceRange.minVariantPrice.amount);
  const onSale = !!(compareAt && price && compareAt > price);

  return {
    onSale,
    quickShip,
    newProduct: daysSince(node.createdAt) < NEW_PRODUCT_DAYS,
    bestSeller,
    merchPick,
    madeInUsa,
    disRevTag,
  };
}

function labelSpans(flags: CardFlags): string {
  const labels: string[] = [];
  if (flags.madeInUsa) labels.push('usa');
  if (flags.onSale) labels.push('sale');
  if (flags.newProduct) labels.push('new');
  if (flags.bestSeller) labels.push('best');
  if (flags.merchPick) labels.push('merch');
  const first = labels[0] || '';
  const second = labels[1] || '';
  const both = second !== '' ? ' both-label' : '';
  const span = (kind: string, cls: string, withBoth: boolean, text?: string) => {
    if (kind === '') return '';
    return '<span class="' + cls + (withBoth ? both : '') + '">' + (text || '') + '</span>';
  };
  const render = (kind: string, withBoth: boolean) => {
    switch (kind) {
      case 'sale':
        return span(kind, 'sale-label', withBoth);
      case 'new':
        return withBoth && first === 'sale'
          ? '<span class="new-label-with-sale"></span>'
          : span(kind, 'new-label', withBoth);
      case 'best':
        return span(kind, 'best-seller', withBoth);
      case 'merch':
        return span(kind, 'merch-pick', withBoth);
      case 'usa':
        return span(kind, 'made-in-usa', withBoth, 'MADE IN THE USA');
      default:
        return '';
    }
  };
  return render(first, true) + (second ? render(second, false) : '');
}

function colorSwatches(node: ProductNode, ctx: PageContext): string {
  const tags = node.tags || [];
  const colorTags = ctx.colorTags || [];
  const hexMap = ctx.colorHexMap || {};

  let currentColor = '';
  for (let i = 0; i < tags.length; i++) {
    if (colorTags.indexOf(tags[i]) !== -1) {
      currentColor = tags[i];
      break;
    }
  }
  if (!currentColor) return '';

  const currentSlug = handleize(currentColor);
  const baseHandle = node.handle || '';
  let count = 0;
  let slides = '';

  for (let i = 0; i < tags.length; i++) {
    const t = tags[i];
    if (t.indexOf('Series-') !== 0) continue;
    const colorName = t.slice('Series-'.length);
    const siblingSlug = handleize(colorName);
    const siblingHandle = baseHandle.split(currentSlug).join(siblingSlug);
    const link = '/products/' + siblingHandle;
    const bg = hexMap[colorName] || '#cccccc';
    count++;
    slides +=
      '<div class="colorChoiceSlideColl">' +
      '<a href="' + esc(link) + '" aria-label="Click to select ' + esc(colorName) + ' color choice">' +
      '<div data-color-title="' + esc(colorName) + '" data-color-link="' + esc(link) + '"' +
      ' data-toggle="tooltip" data-container=".color-choices-wrap" data-placement="bottom"' +
      ' class="color-circle" style="background: ' + bg + '; background-size: contain;"></div>' +
      '</a></div>';
  }

  if (count === 0) return '';
  const plural = count === 1 ? '' : 's';
  return (
    '<div class="color-choices-wrap"><div class="color-choices-pr-coll">' +
    '<div class="colorChoiceSlider-coll">' + slides + '</div></div>' +
    '<div id="product-' + numericId(node.id) + '-color-count" class="color-count">' +
    '<span>' + count + '</span> color' + plural + '</div></div>'
  );
}

function minQtyNote(flags: CardFlags, ctx: PageContext): string {
  if (!flags.disRevTag || !ctx.discounts) return '';
  const entry = ctx.discounts[flags.disRevTag];
  if (!entry || !entry.range) return '';
  const firstKey = Object.keys(entry.range)[0];
  if (!firstKey) return '';
  return '<span class="minqty"> | Min Qty ' + esc(firstKey) + '</span>';
}

export function buildCardHtml(node: ProductNode, ctx: PageContext): string {
  const flags = readFlags(node);
  const url = '/products/' + esc(node.handle);
  const img = cardImageUrl(node.featuredImage && node.featuredImage.url);
  const price = node.priceRange ? formatPrice(node.priceRange.minVariantPrice.amount) : '';
  const path = ctx.requestPath || '';
  const showCompare =
    path.indexOf('unrl-bundle-collection') === -1 && path.indexOf('merchperks') === -1;

  const quickShipSpan = flags.quickShip ? '<span class="quick-ship">QUICK SHIP</span>' : '';

  return (
    '<div class="col-xs-12 col-sm-6 col-md-4 col-lg-3 no-padding collection-product-list">' +
    '<div class="product-list-inner"><div class="product-area clearfix">' +
    '<div class="product-info" id="product-' + numericId(node.id) + '" data-product-id="' + numericId(node.id) + '" data-gid="' + esc(node.id) + '">' +
    (showCompare ? '<div class="compare">Compare</div>' : '') +
    quickShipSpan +
    labelSpans(flags) +
    '<div class="product-thumbnail"><a href="' + url + '" id="' + esc(node.title) + '">' +
    '<img loading="lazy" src="' + esc(img) + '" alt="' + esc(node.title) + '" width="210" height="210"></a></div>' +
    '<div class="product-info-wrap">' +
    '<div class="price"><span class="product-price">$' + esc(price) + '</span>' +
    '<span class="msrp">MSRP</span>' + minQtyNote(flags, ctx) + '</div>' +
    '<h4><a href="' + url + '" id="' + esc(node.title) + '">' + esc(node.title) + '</a></h4>' +
    colorSwatches(node, ctx) +
    '</div></div></div><div class="sep"></div></div></div>'
  );
}
